import { useSelector } from "react-redux";
import * as yup from "yup";
import { useFormik } from "formik";
import { isEqual } from "lodash";
import { getIsCommercialOrBusinessApplication, REFERRAL_FEE } from "@/constants";
import { InArrears, FeeFrequency, RepaymentFrequency } from "@/store/slices/types/applicationFormSlice";
import type { ApplicationFormSlice, ApplicationState, Fee } from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import { getSelectableFeesList } from "@/utils/lenderUtils";
import type { LenderProduct } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";

// This extended processingFeeFrequency follows the frequency of the repayments rather than the frequency of the fees.
// It is unique to the Payment Processing Fee.
interface CustomQuoteFee extends Fee {
  processingFeeFrequency?: RepaymentFrequency;
}

interface QuoteCalculatorFormFields {
  rate: number | undefined;
  brokerage: number | undefined;
  inArrears: InArrears;
  repaymentFrequency: RepaymentFrequency;
  fees: CustomQuoteFee[];
  selectFee: string;
  customFee?: string;
  selectableFeesList: Fee[];
  referrer: string;
  lenderDefaultFees: boolean | undefined;
  // New fields for manual quote.
  lender?: string | undefined;
  effectiveRate?: number | undefined;
  repayments?: number | undefined;
}

// The figures that make a quote custom. In arrears is left out because the default and custom quotes share it.
// The masked inputs echo values set in code back as two-decimal strings, so amounts are compared as numbers.
const getQuoteFigures = ({ rate, brokerage, repaymentFrequency, referrer, fees }: QuoteCalculatorFormFields) => ({
  rate: Number(rate ?? 0),
  brokerage: Number(brokerage ?? 0),
  repaymentFrequency,
  referrer: referrer ?? "",
  fees: fees.map(({ key, value, frequency }) => ({ key, value: Number(value ?? 0), frequency })),
});

const useQuoteCalculatorForm = ({ product }: { product: LenderProduct | undefined }) => {
  const { applicationType, loanDetails } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const { lender } = product ?? {};
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);

  const isLoanDetailsFromSelectedLender = loanDetails.lender === lender;
  // Loan details saved for this lender are the quote already chosen for it, so they start out as its custom quote.
  const isSavedQuoteFromSelectedLender = !!product && isLoanDetailsFromSelectedLender && loanDetails.rate != null;

  const referralFeeFromLoanDetails = (loanDetails.fees || []).find(
    (fee) => fee.key === REFERRAL_FEE || fee.name?.toLowerCase().includes("referral"),
  );

  const initialValuesFromLoanDetails = {
    rate: loanDetails.rate,
    // Consumer and personal quotes have no brokerage field, but still carry the saved brokerage as their commission.
    // Without it they would show, and save again, a commission of zero.
    brokerage: loanDetails.brokerage,
    inArrears: loanDetails.inArrears ?? InArrears.Yes,
    // ## TODO: (FeeFrequency) When the fee frequency has changed, we do not need to convert the lettercase.
    fees: (loanDetails.fees || []).map((fee) => ({
      ...fee,
      frequency: (fee.frequency?.toLowerCase() || FeeFrequency.Upfront) as FeeFrequency,
    })),
    selectableFeesList: getSelectableFeesList(loanDetails.fees || []),
    referrer: referralFeeFromLoanDetails?.referrer ?? loanDetails.referrer ?? "",
  };

  const initialValues: QuoteCalculatorFormFields = {
    rate: 0,
    brokerage: 0,
    inArrears: InArrears.Yes,
    repaymentFrequency: RepaymentFrequency.Monthly,
    fees: [],
    selectFee: "",
    selectableFeesList: getSelectableFeesList([]),
    referrer: "",
    lenderDefaultFees: undefined,
    // If no product (manual quote), or loan details are from selected lender, then initial values are from loan details.
    ...((!product || isLoanDetailsFromSelectedLender) && initialValuesFromLoanDetails),
    ...(isSavedQuoteFromSelectedLender && { lenderDefaultFees: false }),
  };

  const validationSchema = yup.object({
    rate: yup.string().required("Rate is required").matches(regex.percentageRegex, "Please enter valid percentage."),
    ...(isCommercialOrBusinessApplication && {
      brokerage: yup
        .string()
        .required("Brokerage is required")
        .matches(regex.percentageRegex, "Please enter valid percentage."),
    }),
  });

  const quoteCalculatorFormik = useFormik({
    initialValues,
    onSubmit: () => {},
    validationSchema,
  });

  // Custom once the broker changes a figure from where the calculator started (blank, the saved loan details, or the
  // lender's defaults after "Use Lender Default"), or while it holds customised fees or a saved quote.
  // Opening or closing the calculator alone never makes a quote custom.
  const isCustomQuote =
    quoteCalculatorFormik.values.lenderDefaultFees === false ||
    !isEqual(getQuoteFigures(quoteCalculatorFormik.initialValues), getQuoteFigures(quoteCalculatorFormik.values));

  return { quoteCalculatorFormik, isCustomQuote };
};

export { useQuoteCalculatorForm };
export type { QuoteCalculatorFormFields };
